/**
 * Send to Appeal column badge-button values for oncology submission.
 */

import { TableCellWithBadge } from "../../../data-model/tableCells";
import { PaStatus } from "../../../enums/paStatus";

/** API fields used to choose Send to Appeal column label and styling. */
interface SendToAppealBadgeButtonInput {
  responseStatus?: string | null;
  appealStatus?: string | null;
}

/**
 * Returns whether the row already has an appeal status recorded.
 *
 * @param appealStatus - Row `appeal_status` value from the API
 * @returns True when `appeal_status` is not null
 */
const hasAppealStatus = (
  appealStatus: string | null | undefined,
): boolean => appealStatus != null;

/**
 * Returns whether the row's PA Status is Denied.
 *
 * @param responseStatus - Row `response_status` value
 * @returns True when PA Status is Denied
 */
const isDeniedPaStatus = (
  responseStatus: string | null | undefined,
): boolean => {
  if (!responseStatus) {
    return false;
  }

  return responseStatus.trim().toLowerCase() === PaStatus.DENIED.toLowerCase();
};

/**
 * Builds the badge-button cell value for the Send to Appeal column.
 * Shows an enabled grey "View" when `appeal_status` is set; otherwise an enabled
 * black "Send" when PA Status is Denied, or a disabled "Send".
 *
 * @param input - PA and appeal status fields from the order row
 * @returns Value for the badge-button table cell (`BadgeButton` component)
 */
export const getSendToAppealBadgeButtonValue = (
  input: SendToAppealBadgeButtonInput,
): TableCellWithBadge => {
  if (hasAppealStatus(input.appealStatus)) {
    return {
      text: "View",
      color: "#111111",
      bgColor: "#F5F5F5",
      isDisabled: false,
    };
  }

  const isEnabled = isDeniedPaStatus(input.responseStatus);

  return {
    text: "Send",
    color: "#FFFFFF",
    bgColor: "#111111",
    isDisabled: !isEnabled,
  };
};
